import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import cmsApi from "../../services/cmsApi";
import { getImageUrl } from "../../utils/getImageUrl";

const timeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 172800) return "Yesterday";
  return date.toLocaleDateString();
};

const Badge = ({ priority }) => {
  const map = {
    breaking: "bg-red-600 text-white",
    urgent: "bg-amber-500 text-white",
    update: "bg-blue-600 text-white",
    announcement: "bg-emerald-600 text-white",
    normal: "bg-gray-300 text-gray-800",
  };
  const label =
    priority === "breaking"
      ? "Breaking"
      : priority.charAt(0).toUpperCase() + priority.slice(1);
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-semibold ${map[priority] || map.normal}`}
    >
      {label}
    </span>
  );
};

const ShareButton = ({ url, title }) => {
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard");
      }
    } catch {}
  };
  return (
    <button
      onClick={share}
      className="text-sm text-white/90 hover:text-white transition-colors"
    >
      Share
    </button>
  );
};

const SkeletonCard = () => (
  <div className="rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 overflow-hidden animate-pulse">
    <div className="h-40 sm:h-48 bg-white/10" />
    <div className="p-4">
      <div className="h-4 bg-white/10 rounded w-2/3 mb-2" />
      <div className="h-3 bg-white/10 rounded w-full mb-1.5" />
      <div className="h-3 bg-white/10 rounded w-5/6" />
    </div>
  </div>
);

const BreakingNewsUpdates = React.memo(() => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tickerOpen, setTickerOpen] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  
  // CMS Settings
  const [sectionTitle, setSectionTitle] = useState("Breaking News & Latest Updates");
  const [allNewsButtonText, setAllNewsButtonText] = useState("View All News");
  const [allNewsButtonLink, setAllNewsButtonLink] = useState("/news");
  const [tickerTitle, setTickerTitle] = useState("Breaking Ticker");
  const [tickerInterval, setTickerInterval] = useState(5000);
  const [tickerMinPriority, setTickerMinPriority] = useState("normal");

  const prefersReducedMotion = useMemo(
    () =>
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  // Handle navigation to detail route
  const handleNewsClick = (newsId) => {
    if (!newsId) {
      console.warn("News identifier is missing; redirecting to /news");
      navigate("/news");
      return;
    }
    navigate(`/news/${newsId}`);
  };

  // Fetch CMS settings for breaking-news component
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const fetchCMSSettings = async () => {
      try {
        const result = await cmsApi.getHomepageComponentByType('breaking-news');
        if (result.success && result.components.length > 0) {
          const component = result.components[0];
          const settings = component.settings || {};
          
          setSectionTitle(component.title || "Breaking News & Latest Updates");
          setAllNewsButtonText(settings.allNewsButtonText || "View All News");
          setAllNewsButtonLink(settings.allNewsButtonLink || "/news");
          setTickerTitle(settings.tickerTitle || "Breaking Ticker");
          setTickerInterval(settings.tickerInterval || 5000);
          setTickerMinPriority(settings.tickerMinPriority || "normal");
          
          // Set initial ticker state from CMS
          setTickerOpen(settings.showTickerOnLoad !== false);
        }
      } catch (error) {
        console.log('Using default breaking-news settings:', error.message);
      }
    };

    fetchCMSSettings();
  }, []);

  // Fetch breaking news from centralized News API ONLY
  // Single source of truth: News Management Module
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");
        
        // Fetch breaking news from News API (breaking: true)
        // The API filters for isBreaking: true OR we can filter by priority: 'breaking'
        const result = await cmsApi.getNews({ limit: 50, breaking: 'true' });
        
        if (result.success && result.news && Array.isArray(result.news) && result.news.length > 0) {
          // The API already filtered for isBreaking: true, but also include items with priority: 'breaking'
          // This ensures we catch all breaking news items
          const breakingNews = result.news.filter(article => {
            // Include if API already filtered it (isBreaking: true) OR if priority is 'breaking'
            return article.isBreaking === true || article.priority === 'breaking';
          });
          
          if (breakingNews.length > 0) {
            // Sort by most recent publishedAt
            const sorted = [...breakingNews].sort((a, b) => {
              const ta = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
              const tb = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
              return tb - ta;
            });
            
            // Enrich with image URLs using getImageUrl utility
            const enriched = sorted.map((n) => ({
              ...n,
              thumbnail: getImageUrl(n.thumbnail || n.featuredImage || n.image || ''),
              image: getImageUrl(n.image || n.featuredImage || ''),
              featuredImage: getImageUrl(n.featuredImage || ''),
            }));
            
            if (!cancelled) {
              setNews(enriched);
            }
          } else {
            // No breaking news found in filtered results - try fetching all news
            const allNewsResult = await cmsApi.getNews({ limit: 50 });
            if (allNewsResult.success && allNewsResult.news && Array.isArray(allNewsResult.news)) {
              // Filter for breaking news by priority or isBreaking flag
              const allBreakingNews = allNewsResult.news.filter(article => {
                return article.priority === 'breaking' || article.isBreaking === true;
              });
              
              if (allBreakingNews.length > 0) {
                const sorted = [...allBreakingNews].sort((a, b) => {
                  const ta = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
                  const tb = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
                  return tb - ta;
                });
                
                const enriched = sorted.map((n) => ({
                  ...n,
                  thumbnail: getImageUrl(n.thumbnail || n.featuredImage || n.image || ''),
                  image: getImageUrl(n.image || n.featuredImage || ''),
                  featuredImage: getImageUrl(n.featuredImage || ''),
                }));
                
                if (!cancelled) {
                  setNews(enriched);
                }
              } else {
                // No breaking news found - show empty state
                if (!cancelled) {
                  setNews([]);
                  setError("");
                }
              }
            } else {
              // API returned no results
              if (!cancelled) {
                setNews([]);
                setError("");
              }
            }
          }
        } else {
          // API returned no results or failed - try fetching all news
          try {
            const allNewsResult = await cmsApi.getNews({ limit: 50 });
            if (allNewsResult.success && allNewsResult.news && Array.isArray(allNewsResult.news)) {
              // Filter for breaking news by priority or isBreaking flag
              const breakingNews = allNewsResult.news.filter(article => {
                return article.priority === 'breaking' || article.isBreaking === true;
              });
              
              if (breakingNews.length > 0) {
                const sorted = [...breakingNews].sort((a, b) => {
                  const ta = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
                  const tb = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
                  return tb - ta;
                });
                
                const enriched = sorted.map((n) => ({
                  ...n,
                  thumbnail: getImageUrl(n.thumbnail || n.featuredImage || n.image || ''),
                  image: getImageUrl(n.image || n.featuredImage || ''),
                  featuredImage: getImageUrl(n.featuredImage || ''),
                }));
                
                if (!cancelled) {
                  setNews(enriched);
                }
              } else {
                // No breaking news found - show empty state
                if (!cancelled) {
                  setNews([]);
                  setError("");
                }
              }
            } else {
              // API returned no results
              if (!cancelled) {
                setNews([]);
                setError("");
              }
            }
          } catch (fallbackError) {
            console.error('Fallback fetch failed:', fallbackError);
            if (!cancelled) {
              setNews([]);
              setError("");
            }
          }
        }
      } catch (e) {
        console.error("Failed to load breaking news from News API:", e);
        setError("Failed to load breaking news. Please ensure news items are published in the News Management Module.");
        if (!cancelled) {
          setNews([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    if (paused) return;
    if (!news.length) return;
    intervalRef.current = setInterval(() => {
      setActiveIndex((i) => (i + 1) % news.length);
    }, tickerInterval);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [paused, news.length, prefersReducedMotion, tickerInterval]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("bn-visible");
          }
        });
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Filter news for ticker based on priority
  const priorityOrder = { breaking: 4, urgent: 3, update: 2, normal: 1 };
  const minPriorityLevel = priorityOrder[tickerMinPriority] || 1;
  const tickerNews = news.filter((n) => {
    const priority = n.priority || 'normal';
    const priorityLevel = priorityOrder[priority] || 1;
    return priorityLevel >= minPriorityLevel;
  });

  const featured = news[activeIndex];
  const others = news.filter((_, i) => i !== activeIndex).slice(0, 3);

  return (
    <section
      ref={sectionRef}
      className="relative w-full py-10 sm:py-14 bg-gradient-to-br from-custom-pink via-purple-900 to-indigo-900 text-white overflow-hidden min-[1024px]:max-[1535px]:!py-8"
    >
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        aria-hidden
      >
        <div className="absolute -top-16 -left-16 w-64 h-64 bg-gold rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-custom-pink rounded-full blur-3xl" />
      </div>

      {/* Top Ticker */}
      {tickerOpen && tickerNews.length > 0 && (
        <div className="w-full mb-6 px-4 sm:px-6 lg:px-10">
          <div className="relative rounded-xl bg-white backdrop-blur-md border border-gold overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600 animate-pulse-glow" />
                <span className="text-sm font-bold text-black">
                  {tickerTitle}
                </span>
              </div>
              <button
                onClick={() => setTickerOpen(false)}
                className="text-sm text-black hover:text-black font-bold"
              >
                Hide
              </button>
            </div>
            {/* Separator line between title and ticker items */}
            <div className="border-t border-gold/60" />
            <div
              className="bn-ticker select-none"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <div className="bn-ticker-track">
                {tickerNews.map((n) => (
                  <div key={n.id} className="bn-ticker-item">
                    <Badge priority={n.priority || "update"} />
                    <span className="mx-2 text-black">{n.title}</span>
                    <span className="text-black font-bold">
                      • {timeAgo(n.publishedAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {!tickerOpen && (
        <div className="w-full mb-6 px-4 sm:px-6 lg:px-10">
          <div className="flex justify-end">
            <button
              onClick={() => setTickerOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-black border border-gold hover:shadow transition"
            >
              Show Ticker
            </button>
          </div>
        </div>
      )}

      <div className="w-full px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between mb-6 min-[1024px]:max-[1535px]:!mb-4">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-wide min-[1024px]:max-[1535px]:!text-xl">
            {sectionTitle}
          </h2>
          <Link
            to={allNewsButtonLink}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-gold to-yellow-500 text-black font-bold hover:shadow-lg transition-shadow text-xs sm:text-sm min-[1024px]:max-[1535px]:!px-3 min-[1024px]:max-[1535px]:!py-1.5 min-[1024px]:max-[1535px]:!text-xs"
          >
            {allNewsButtonText}
          </Link>
        </div>

        {/* Loading / Error / Empty States */}
        {loading && (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <SkeletonCard />
            </div>
            <SkeletonCard />
          </div>
        )}
        {!loading && error && (
          <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/10 p-6 text-white/90">
            {error}
          </div>
        )}
        {!loading && !error && news.length === 0 && (
          <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/10 p-6 text-white/90">
            No news available right now. Please check back later.
          </div>
        )}

        {!loading && !error && news.length > 0 && (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch min-[1024px]:max-[1535px]:!gap-4">
            {/* Featured Card */}
            <Link
              to={`/news/${featured?.slug || featured?.id || ''}`}
              className="md:col-span-2 group rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 overflow-hidden relative hover:shadow-2xl hover:bg-white/15 transition-all cursor-pointer block"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight")
                  setActiveIndex((i) => (i + 1) % news.length);
                if (e.key === "ArrowLeft")
                  setActiveIndex((i) => (i - 1 + news.length) % news.length);
                if (e.key === "Enter") {
                  handleNewsClick(featured?.id);
                }
              }}
              aria-label={`Read full article: ${featured?.title}`}
            >
              <div className="relative h-72 sm:h-96 lg:h-[30rem] overflow-hidden">
                <img
                  src={getImageUrl(featured?.thumbnail || featured?.featuredImage || featured?.image || '') || 
                    `https://source.unsplash.com/random/800x600?education,campus`}
                  alt={featured?.title || "News image"}
                  className="w-full h-full object-cover bn-img-blur group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  onError={(e) => {
                    console.error('Failed to load featured news image:', featured?.thumbnail || featured?.featuredImage);
                    e.currentTarget.src = `https://source.unsplash.com/random/800x600?education,campus`;
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                {featured?.priority === "breaking" && (
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600 text-white text-xs font-bold animate-pulse-glow">
                      Breaking
                    </span>
                  </div>
                )}
              </div>
              <div className="p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Badge priority={featured?.priority || "update"} />
                  <span className="text-white/80 text-sm">
                    {timeAgo(featured?.publishedAt)}
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-extrabold leading-snug mb-2 group-hover:underline">
                  {featured?.title}
                </h3>
                <p className="text-white/85 mb-4">
                  {featured?.excerpt?.length > 180
                    ? featured.excerpt.slice(0, 180) + "…"
                    : featured?.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 group-hover:bg-white/25 transition-colors">
                    Read more
                  </span>
                  <ShareButton
                    url={`${window.location.origin}/news/${featured?.slug || featured?.id || ''}`}
                    title={featured?.title}
                  />
                </div>
              </div>
            </Link>

            {/* Secondary Cards - Fixed vertical centering */}
            <div className="grid gap-6">
              {others.map((n, idx) => (
                <Link
                  key={n.id}
                  to={`/news/${n.slug || n.id}`}
                  className="rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 overflow-hidden hover:bg-white/15 hover:shadow-lg transition-all cursor-pointer block group"
                  aria-label={`Read full article: ${n.title}`}
                >
                  <div className="flex h-full">
                    <div className="flex-shrink-0">
                      <img
                        src={getImageUrl(n.thumbnail || n.featuredImage || n.image || '') || 
                          `https://source.unsplash.com/collection/190727/300x300?sig=${idx}&education`}
                        alt={n.title}
                        className="w-28 h-28 sm:h-full sm:min-h-[112px] object-cover hidden sm:block group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => {
                          console.error('Failed to load news image:', n.thumbnail || n.featuredImage);
                          e.currentTarget.src = `https://source.unsplash.com/collection/190727/300x300?sig=${idx}&education`;
                        }}
                      />
                    </div>
                    {/* Fixed: Added flex and justify-center to center content vertically */}
                    <div className="p-4 flex-1 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge priority={n.priority || "update"} />
                        {new Date().getTime() -
                          new Date(n.publishedAt).getTime() <
                          24 * 3600 * 1000 && (
                          <span className="px-2 py-0.5 rounded bg-emerald-500 text-white text-[10px] font-bold">
                            NEW
                          </span>
                        )}
                        <span className="text-white/70 text-xs ml-auto">
                          {timeAgo(n.publishedAt)}
                        </span>
                      </div>
                      <h4 className="font-semibold group-hover:underline block mb-1 text-sm sm:text-base leading-tight">
                        {n.title}
                      </h4>
                      <p className="text-white/80 text-sm line-clamp-2">
                        {n.excerpt}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
});

export default BreakingNewsUpdates;
